// Exceptions — the pharmacist's decision surface for orders that
// landed in a state requiring human attention.
//
// v1 buckets (from list_exceptions RPC / PR #111):
//   - failed_delivery  — orders.status = 'failed', no live decision
//   - missing_pod      — delivered orders without a live pod_records row (2h grace)
//   - aged_pending     — orders.status = 'pending', created > 24h ago
//
// v1 decisions (per bucket):
//   - failed / aged_pending: retry_tomorrow, hold_for_pickup,
//       return_to_shelf, cancel_refund, reassign_address
//   - missing_pod:           request_pod_reupload, attest_delivered, escalate
//
// Position in the product: pharmacies open LYN Rx and ask "what needs
// my eyes today?" — this is where the answer lives. Every decision
// writes an audit_events row (`order.exception_decisioned`) so the
// compliance trail is intact; the surface itself is the working
// counterpart to the audit log.
//
// v1 deliberately scoped down (deferred to PR #114+):
//   - Bulk actions (select N cards → apply same decision)
//   - Sidebar count badge for unread items
//   - Notification / email digest on new exceptions
//   - Cold-chain deviation + geofence-override buckets
//   - Additional filter dimensions (date range, driver, pharmacy)

import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { LynrxSupabaseClient } from '@lynrx/shared';
import { useAuth } from '../lib/auth/auth-context';
import { requireUserType } from '../lib/auth/guards';
import { supabase as defaultClient } from '../lib/supabase';
import { captureSupabaseError } from '../lib/sentry-supabase';
import { Alert } from '../components/ui/Alert';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { useSetPageHeader } from '../components/layout/PageHeaderContext';
import { publishExceptionsCount } from '../lib/exceptions/count-store';
import type { Database } from '@lynrx/shared';
import { CustomerInquiriesView } from '../components/inquiries/CustomerInquiriesView';

export const Route = createFileRoute('/_authenticated/exceptions')({
  // Route-level role gate — pharmacies own the compliance decision.
  // Dispatchers are excluded here (they may get a separate driver-
  // facing follow-up flow); platform_admin passes through via the
  // LYN-staff override inside requireUserType. RLS on order_exceptions
  // + orders remains the safety net under the queries.
  beforeLoad: async () => {
    await requireUserType({ allowed: ['pharmacy_admin', 'pharmacy_staff'] });
  },
  component: ExceptionsPage,
});

function ExceptionsPage(): JSX.Element {
  useSetPageHeader({
    title: 'Follow-ups',
    subtitle: 'Orders waiting on a pharmacist decision · patient inquiries.',
  });
  return <ExceptionsView />;
}

// ----------------------------------------------------------------------------
// Types + bucket-decision mapping.

type Bucket = Database['public']['Enums']['order_exception_type'];
type Decision = Database['public']['Enums']['order_exception_decision'];
type ReasonCode = Database['public']['Enums']['order_exception_reason_code'];

type ExceptionRow = {
  order_id: string;
  pharmacy_id: string;
  order_number: string;
  patient_name: string | null;
  bucket: Bucket;
  raised_at: string;
  order_status: Database['public']['Enums']['order_status'];
};

// DEV-only fixture for the `?demo=1` walk-through. Names + order
// numbers are obviously fake ("Doe, Jane" / "RX-DEMO-*"). Times are
// computed relative to render so ages read naturally regardless of
// when you open the page. NEVER used in prod — the demo mode is
// gated on `import.meta.env.DEV` and tree-shakes out.
const DEMO_ROWS: ExceptionRow[] = [
  {
    order_id: 'demo-1',
    pharmacy_id: 'demo-pha',
    order_number: 'RX-DEMO-0001',
    patient_name: 'Doe, Jane',
    bucket: 'failed_delivery',
    raised_at: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
    order_status: 'failed',
  },
  {
    order_id: 'demo-2',
    pharmacy_id: 'demo-pha',
    order_number: 'RX-DEMO-0002',
    patient_name: 'Rivera, Marcus',
    bucket: 'failed_delivery',
    raised_at: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
    order_status: 'failed',
  },
  {
    order_id: 'demo-3',
    pharmacy_id: 'demo-pha',
    order_number: 'RX-DEMO-0003',
    patient_name: null,
    bucket: 'missing_pod',
    raised_at: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
    order_status: 'delivered',
  },
  {
    order_id: 'demo-4',
    pharmacy_id: 'demo-pha',
    order_number: 'RX-DEMO-0004',
    patient_name: 'Kowalski, Ada',
    bucket: 'aged_pending',
    raised_at: new Date(Date.now() - 30 * 60 * 60 * 1000).toISOString(),
    order_status: 'pending',
  },
  {
    order_id: 'demo-5',
    pharmacy_id: 'demo-pha',
    order_number: 'RX-DEMO-0005',
    patient_name: 'Nakamura, Ben',
    bucket: 'aged_pending',
    raised_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    order_status: 'pending',
  },
  {
    order_id: 'demo-6',
    pharmacy_id: 'demo-pha',
    order_number: 'RX-DEMO-0006',
    patient_name: 'O’Brien, Sean',
    bucket: 'failed_delivery',
    raised_at: new Date(Date.now() - 20 * 60 * 1000).toISOString(),
    order_status: 'failed',
  },
  {
    order_id: 'demo-7',
    pharmacy_id: 'demo-pha',
    order_number: 'RX-DEMO-0007',
    patient_name: 'Nakamura, Ben',
    bucket: 'cold_chain_deviation',
    raised_at: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
    order_status: 'delivered',
  },
];

// Bucket → allowed decisions. Mirrors the SQL guard in
// decide_exception (PR #111) — keeping the two in sync prevents the
// client from offering a decision the RPC will reject as
// LYNRX_INVALID_DECISION_FOR_TYPE.
const DECISIONS_BY_BUCKET: Record<Bucket, readonly Decision[]> = {
  failed_delivery: [
    'retry_tomorrow',
    'hold_for_pickup',
    'return_to_shelf',
    'cancel_refund',
    'reassign_address',
  ],
  aged_pending: [
    'retry_tomorrow',
    'hold_for_pickup',
    'return_to_shelf',
    'cancel_refund',
    'reassign_address',
  ],
  missing_pod: ['request_pod_reupload', 'attest_delivered', 'escalate'],
  // Cold-chain deviation reuses existing decisions rather than
  // inventing bucket-specific vocabulary — no pharmacy customer has
  // asked for cold-chain-specific verbs yet, and generic-attest +
  // cancel-refund + escalate covers the compliance-relevant paths
  // (accept the risk, reject and re-dispense, kick to the state
  // board). Add cold-chain-specific decisions when a customer names
  // them.
  cold_chain_deviation: ['attest_delivered', 'cancel_refund', 'escalate'],
};

// Decision → reason codes that make sense for it. Presented as the
// select's option list; users can still pick "other" and drop the
// specifics in the notes field. Kept generous — the audit posture
// benefits from ANY reason code over none.
const REASONS_BY_DECISION: Record<Decision, readonly ReasonCode[]> = {
  retry_tomorrow: ['patient_not_home', 'wrong_address', 'weather', 'other'],
  hold_for_pickup: ['patient_not_home', 'other'],
  return_to_shelf: [
    'patient_hospitalized',
    'patient_deceased',
    'no_longer_needed',
    'patient_refused',
    'other',
  ],
  cancel_refund: [
    'patient_refused',
    'no_longer_needed',
    'patient_hospitalized',
    'other',
  ],
  reassign_address: ['wrong_address', 'other'],
  request_pod_reupload: ['driver_forgot_pod', 'driver_uploaded_wrong_photo', 'other'],
  attest_delivered: ['pharmacist_manual_verification', 'other'],
  escalate: ['other'],
};

// Taxonomy note (2026-08-19): the underlying enum value is
// `failed_delivery`, but the UI relabels it "Returned" — the current
// bucket covers patient-not-home / refused / wrong-address / weather,
// which are all cases where the package came back to the pharmacy
// (i.e., a return). True "failed" — wrong recipient / wrong medication
// / compliance incident — will land as a NEW bucket in a follow-up PR.
// Schema stays as-is to keep the audit trail continuous; only the
// display label changed.
const BUCKET_LABELS: Record<Bucket, string> = {
  failed_delivery: 'Returned',
  missing_pod: 'Missing POD',
  aged_pending: 'Aged pending',
  cold_chain_deviation: 'Cold chain',
};

const DECISION_LABELS: Record<Decision, string> = {
  retry_tomorrow: 'Retry tomorrow',
  hold_for_pickup: 'Hold for pickup',
  return_to_shelf: 'Return to shelf',
  cancel_refund: 'Cancel & refund',
  reassign_address: 'Reassign to new address',
  request_pod_reupload: 'Request POD re-upload',
  attest_delivered: 'Attest as delivered',
  escalate: 'Escalate to LYN Rx',
};

const REASON_LABELS: Record<ReasonCode, string> = {
  patient_not_home: 'Patient not home',
  wrong_address: 'Wrong address',
  patient_refused: 'Patient refused delivery',
  weather: 'Weather / road conditions',
  patient_hospitalized: 'Patient hospitalized',
  patient_deceased: 'Patient deceased',
  no_longer_needed: 'No longer needed',
  driver_forgot_pod: 'Driver forgot POD capture',
  driver_uploaded_wrong_photo: 'Driver uploaded wrong photo',
  pharmacist_manual_verification: 'Pharmacist verified manually',
  other: 'Other (see notes)',
};

const BUCKET_FILTER_ORDER: readonly Bucket[] = [
  'failed_delivery',
  'missing_pod',
  'cold_chain_deviation',
  'aged_pending',
];

// ----------------------------------------------------------------------------
// Time formatting — "raised X ago" gives an at-a-glance sense of
// urgency. Local calendar, human-readable buckets.

function timeAgo(iso: string, now: Date = new Date()): string {
  const then = new Date(iso).getTime();
  const diffMs = now.getTime() - then;
  const min = Math.floor(diffMs / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return `${Math.floor(day / 7)}w ago`;
}

// ----------------------------------------------------------------------------
// View — extractable for tests. Client injected for stub-ability.

type ViewProps = {
  client?: LynrxSupabaseClient;
};

// Follow-ups filter surface. Bucket enum values map to the exception
// list; 'all' rolls up every exception bucket; 'customer_inquiries' is
// a peer chip that renders the CustomerInquiriesView in place of the
// exception cards (distinct data model, distinct workflow, unified
// filter row for one consistent UX).
type FollowupFilter = Bucket | 'all' | 'customer_inquiries';

export function ExceptionsView({ client = defaultClient }: ViewProps): JSX.Element {
  const { session, isReady } = useAuth();

  // Bucket filter — 'all' means unfiltered. Kept in useState (not
  // URL search params) for v1: TanStack Router search-schema tests
  // require router-context scaffolding this codebase doesn't yet have
  // (same reasoning as PR #108's later commit that walked back
  // ?view=map to useState).
  const [bucketFilter, setBucketFilter] = useState<FollowupFilter>('all');
  // Lightweight COUNT query for the "Customer inquiries" chip. Kept
  // separate from the CustomerInquiriesView so the chip stays
  // accurate even when the user is viewing exception buckets. Null
  // until first fetch resolves.
  const [inquiriesCount, setInquiriesCount] = useState<number | null>(null);

  const [rows, setRows] = useState<ExceptionRow[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [decideFor, setDecideFor] = useState<ExceptionRow | null>(null);

  // Bulk-action selection state. Keyed by order_id (safe because
  // checkboxes only render when a single-bucket filter is active —
  // see cardMode below — so `(order_id, bucket)` collapses to
  // `order_id` in the selectable subset). Cleared on bucket change
  // and after a successful bulk submit.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);

  // DEV-only demo mode — `?demo=1` in the URL swaps live data for a
  // handful of hard-coded rows so we can screenshot / walk through
  // the populated state without touching prod. Tree-shaken from
  // production builds by the `import.meta.env.DEV` gate; the demo
  // rows never touch the database. See DEMO_ROWS below for the
  // fixture. Turn off by removing `?demo=1` from the URL.
  const demoMode =
    import.meta.env.DEV &&
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('demo') === '1';

  const fetchList = useCallback(async () => {
    if (!session) return;
    setLoading(true);
    setLoadError(null);

    if (demoMode) {
      // Skip the RPC entirely — publish the fixture straight to the
      // UI so bucket counts, cards, and the sidebar badge all reflect
      // the demo set.
      setLoading(false);
      setRows(DEMO_ROWS);
      publishExceptionsCount(DEMO_ROWS.length);
      return;
    }

    // p_pharmacy_id NULL — RLS scopes the underlying orders query to
    // whatever the caller can see. Multi-pharmacy tenants filter
    // client-side (post-fetch) via the bucket filter; per-pharmacy
    // filter dimension is a follow-up.
    const { data, error } = await client.rpc('list_exceptions', {});
    setLoading(false);
    if (error) {
      captureSupabaseError(error, 'exceptions.list.fetch');
      setLoadError('Could not load exceptions.');
      // Clear the store back to 'unknown' so the sidebar badge stops
      // asserting a stale count. Refetch on next focus will retry.
      publishExceptionsCount(null);
      return;
    }
    const nextRows = (data ?? []) as ExceptionRow[];
    setRows(nextRows);
    // Push the fresh count to the sidebar badge without a second RPC.
    publishExceptionsCount(nextRows.length);
  }, [client, session, demoMode]);

  useEffect(() => {
    if (!isReady || !session) return;
    void fetchList();
  }, [isReady, session, fetchList]);

  // Client-side counts per bucket for the filter chips. Recomputed on
  // every row change — cheap for the expected working-set size (a
  // pharmacy triaging 5-20 exceptions at a time).
  const counts = useMemo(() => {
    const c: Record<Bucket, number> = {
      failed_delivery: 0,
      missing_pod: 0,
      aged_pending: 0,
      cold_chain_deviation: 0,
    };
    for (const r of rows ?? []) c[r.bucket] += 1;
    return c;
  }, [rows]);

  const visibleRows = useMemo(() => {
    if (!rows) return null;
    if (bucketFilter === 'all') return rows;
    // customer_inquiries has its own view (rendered separately) — no
    // exception rows to filter for that chip.
    if (bucketFilter === 'customer_inquiries') return [];
    return rows.filter((r) => r.bucket === bucketFilter);
  }, [rows, bucketFilter]);

  const total = rows?.length ?? 0;

  // Selection mode is only meaningful when a specific EXCEPTION
  // bucket is active — bulk decisions require a single bucket (the
  // SQL guard rejects mismatched decisions), and it doesn't apply to
  // customer inquiries at all. Keeps the UX unambiguous: filter
  // first, then bulk-decide.
  const selectionEnabled =
    bucketFilter !== 'all' && bucketFilter !== 'customer_inquiries';
  const inquiriesActive = bucketFilter === 'customer_inquiries';

  // One lightweight COUNT query for the chip. Refetches whenever
  // session changes; the CustomerInquiriesView also refetches on its
  // own resolve action, but the chip count is best-effort ambient
  // information — a small race is acceptable and easy to fix later
  // with a shared publishInquiriesCount store.
  useEffect(() => {
    if (!isReady || !session) return;
    let cancelled = false;
    void (async () => {
      // Wrapped in try/catch because the query may throw synchronously
      // in a test env that mocks `client.rpc` but not `client.from`.
      // Ambient chip count is best-effort — a silent miss is
      // preferable to a page-level crash.
      try {
        const result = (await client
          .from('customer_inquiries' as never)
          .select('id', { count: 'exact', head: true })
          .neq('status', 'resolved')) as unknown as {
          count: number | null;
          error: unknown;
        };
        if (cancelled || result.error) return;
        setInquiriesCount(result.count ?? 0);
      } catch {
        // Silent — the chip renders 0 rather than blocking the page.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, session, isReady]);

  // Reset selection whenever the filter or row set changes so we
  // never carry stale ids into a different bucket.
  useEffect(() => {
    setSelected(new Set());
  }, [bucketFilter, rows]);

  const toggleOne = useCallback((orderId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(orderId)) next.delete(orderId);
      else next.add(orderId);
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => setSelected(new Set()), []);

  const selectedRows = useMemo(() => {
    if (!visibleRows) return [];
    return visibleRows.filter((r) => selected.has(r.order_id));
  }, [visibleRows, selected]);

  return (
    <>
      {loadError ? (
        <div className="mb-4">
          <Alert tone="error">{loadError}</Alert>
        </div>
      ) : null}

      <BucketFilterRow
        active={bucketFilter}
        total={total}
        counts={counts}
        inquiriesCount={inquiriesCount}
        onChange={setBucketFilter}
      />

      {inquiriesActive ? (
        <CustomerInquiriesView client={client} />
      ) : (
        <>
          {selectionEnabled && selected.size > 0 ? (
            <BulkActionBar
              count={selected.size}
              onDecide={() => setBulkOpen(true)}
              onClear={clearSelection}
            />
          ) : null}

          {loading && rows === null ? (
            <div className="rounded-lg border border-border bg-bg p-6 text-sm text-ink-3">
              Loading…
            </div>
          ) : visibleRows && visibleRows.length === 0 ? (
            <EmptyState hasAnyRows={total > 0} filter={bucketFilter} />
          ) : (
            <ul className="space-y-2">
              {(visibleRows ?? []).map((row) => (
                <ExceptionCard
                  key={`${row.order_id}:${row.bucket}`}
                  row={row}
                  selectable={selectionEnabled}
                  selected={selected.has(row.order_id)}
                  onToggleSelect={() => toggleOne(row.order_id)}
                  onDecide={() => setDecideFor(row)}
                />
              ))}
            </ul>
          )}
        </>
      )}

      <DecisionModal
        row={decideFor}
        client={client}
        demoMode={demoMode}
        onClose={() => setDecideFor(null)}
        onDecided={() => {
          if (demoMode && decideFor) {
            // In demo mode the RPC is stubbed — mutate the local
            // rows so the decisioned card disappears the same way
            // it would after a real fetchList. Refetch would just
            // replay DEMO_ROWS unchanged. Republish the new count
            // so the sidebar badge stays in sync.
            setRows((prev) => {
              const next = (prev ?? []).filter(
                (r) => !(r.order_id === decideFor.order_id && r.bucket === decideFor.bucket),
              );
              publishExceptionsCount(next.length);
              return next;
            });
            setDecideFor(null);
            return;
          }
          setDecideFor(null);
          void fetchList();
        }}
      />

      <BulkDecisionModal
        open={bulkOpen && selectedRows.length > 0}
        rows={selectedRows}
        client={client}
        demoMode={demoMode}
        onClose={() => setBulkOpen(false)}
        onDecided={() => {
          if (demoMode) {
            const removedIds = new Set(selectedRows.map((r) => r.order_id));
            setRows((prev) => {
              const next = (prev ?? []).filter((r) => !removedIds.has(r.order_id));
              publishExceptionsCount(next.length);
              return next;
            });
            setBulkOpen(false);
            clearSelection();
            return;
          }
          setBulkOpen(false);
          clearSelection();
          void fetchList();
        }}
      />
    </>
  );
}

// ----------------------------------------------------------------------------
// Bucket filter chips.

function BucketFilterRow({
  active,
  total,
  counts,
  inquiriesCount,
  onChange,
}: {
  active: FollowupFilter;
  total: number;
  counts: Record<Bucket, number>;
  // null while the initial count query is in flight — rendered as
  // an em-dash so we don't flash a misleading "0".
  inquiriesCount: number | null;
  onChange: (next: FollowupFilter) => void;
}): JSX.Element {
  return (
    <div className="mb-4 flex flex-wrap items-center gap-2">
      <FilterChip
        label="All"
        count={total}
        active={active === 'all'}
        onClick={() => onChange('all')}
      />
      {BUCKET_FILTER_ORDER.map((b) => (
        <FilterChip
          key={b}
          label={BUCKET_LABELS[b]}
          count={counts[b]}
          active={active === b}
          onClick={() => onChange(b)}
        />
      ))}
      <FilterChip
        label="Customer inquiries"
        count={inquiriesCount ?? 0}
        active={active === 'customer_inquiries'}
        onClick={() => onChange('customer_inquiries')}
      />
    </div>
  );
}

function FilterChip({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}): JSX.Element {
  const cls = active
    ? 'border-primary bg-primary/10 text-ink'
    : 'border-border bg-bg text-ink-2 hover:bg-bg-soft hover:text-ink';
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition ' +
        cls
      }
    >
      <span>{label}</span>
      <span
        className={
          'inline-flex min-w-[1.25rem] items-center justify-center rounded-full px-1.5 text-[10px] ' +
          (active ? 'bg-primary/20 text-ink' : 'bg-bg-soft text-ink-3')
        }
      >
        {count}
      </span>
    </button>
  );
}

// ----------------------------------------------------------------------------
// Per-item card.

function ExceptionCard({
  row,
  selectable,
  selected,
  onToggleSelect,
  onDecide,
}: {
  row: ExceptionRow;
  selectable: boolean;
  selected: boolean;
  onToggleSelect: () => void;
  onDecide: () => void;
}): JSX.Element {
  return (
    <li className="rounded-lg border border-border bg-bg p-3 shadow-card">
      <div className="flex items-center justify-between gap-3">
        {selectable ? (
          <input
            type="checkbox"
            checked={selected}
            onChange={onToggleSelect}
            aria-label={`Select order ${row.order_number}`}
            className="h-4 w-4 shrink-0 cursor-pointer rounded border-border text-primary focus:ring-primary"
          />
        ) : null}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <BucketBadge bucket={row.bucket} />
            <span className="text-[11px] text-ink-3">{timeAgo(row.raised_at)}</span>
          </div>
          <div className="mt-1 text-sm font-medium text-ink">
            {row.patient_name ?? (
              <span className="italic text-ink-3">No name on file</span>
            )}
          </div>
          <div className="mt-0.5 font-mono text-xs text-ink-2">
            Order {row.order_number}
          </div>
        </div>
        <Button variant="secondary" onClick={onDecide}>
          Decide
        </Button>
      </div>
    </li>
  );
}

// ----------------------------------------------------------------------------
// Bulk action bar — appears when items are selected in a bucket-scoped
// view. Kept visually attached to the cards below (same margin) so it
// reads as their control strip rather than a floating action panel.

function BulkActionBar({
  count,
  onDecide,
  onClear,
}: {
  count: number;
  onDecide: () => void;
  onClear: () => void;
}): JSX.Element {
  return (
    <div className="mb-2 flex items-center justify-between rounded-lg border border-primary bg-primary/5 px-3 py-2">
      <span className="text-sm font-medium text-ink">
        {count} selected
      </span>
      <div className="flex items-center gap-2">
        <Button variant="ghost" onClick={onClear}>
          Clear
        </Button>
        <Button onClick={onDecide}>Decide all</Button>
      </div>
    </div>
  );
}

function BucketBadge({ bucket }: { bucket: Bucket }): JSX.Element {
  // Tonal cues borrowed from the delivery-industry vocabulary:
  // failed → red, missing_pod → amber, aged_pending → slate,
  // cold_chain → sky (cool color for cold-chain packaging compliance).
  const tone: Record<Bucket, string> = {
    failed_delivery: 'bg-red-100 text-red-800',
    missing_pod: 'bg-amber-100 text-amber-800',
    aged_pending: 'bg-slate-100 text-slate-700',
    cold_chain_deviation: 'bg-sky-100 text-sky-800',
  };
  return (
    <span
      className={
        'inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ' +
        tone[bucket]
      }
    >
      {BUCKET_LABELS[bucket]}
    </span>
  );
}

// ----------------------------------------------------------------------------
// Empty state.

function EmptyState({
  hasAnyRows,
  filter,
}: {
  hasAnyRows: boolean;
  filter: FollowupFilter;
}): JSX.Element {
  const primary =
    !hasAnyRows
      ? 'Nothing needs your attention.'
      : filter === 'all' || filter === 'customer_inquiries'
        ? 'Nothing needs your attention.'
        : `No ${BUCKET_LABELS[filter].toLowerCase()} items right now.`;
  const secondary =
    !hasAnyRows
      ? 'Follow-ups appear here when a delivery fails, a POD is missing, or an order sits pending too long.'
      : 'Switch buckets to see other pending decisions.';
  return (
    <div className="rounded-lg border border-border bg-bg p-6 text-center">
      <p className="text-sm font-medium text-ink">{primary}</p>
      <p className="mt-1 text-xs text-ink-3">{secondary}</p>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Decision modal — bucket-aware decision picker + reason code +
// notes, calls decide_exception on submit. Row is null when the
// modal is closed; open state is driven by the parent so re-open
// always starts at a clean form.

function DecisionModal({
  row,
  client,
  demoMode = false,
  onClose,
  onDecided,
}: {
  row: ExceptionRow | null;
  client: LynrxSupabaseClient;
  demoMode?: boolean;
  onClose: () => void;
  onDecided: () => void;
}): JSX.Element {
  // Modal is open iff row is set. All internal form state resets on
  // open (via `row` dep on the effect below).
  const open = row !== null;

  const [decision, setDecision] = useState<Decision | ''>('');
  const [reason, setReason] = useState<ReasonCode | ''>('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Reset form each time a new row arrives.
  useEffect(() => {
    if (!row) return;
    const first = DECISIONS_BY_BUCKET[row.bucket][0];
    setDecision(first ?? '');
    const reasons = first ? REASONS_BY_DECISION[first] : [];
    setReason(reasons[0] ?? '');
    setNotes('');
    setSubmitError(null);
  }, [row]);

  const availableDecisions = row ? DECISIONS_BY_BUCKET[row.bucket] : [];
  const availableReasons = decision ? REASONS_BY_DECISION[decision] : [];

  const handleDecisionChange = (next: Decision): void => {
    setDecision(next);
    const reasons = REASONS_BY_DECISION[next];
    // Reset reason to the first valid one so we never submit a
    // reason that doesn't fit the new decision.
    setReason(reasons[0] ?? '');
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!row || !decision || !reason) return;
    setSubmitting(true);
    setSubmitError(null);
    // Demo mode short-circuit — the RPC would reject the fake demo-*
    // order_ids, so skip the round-trip and let the parent drop the
    // row from local state.
    if (demoMode) {
      setSubmitting(false);
      onDecided();
      return;
    }
    // Build args conditionally so p_notes is only present when non-
    // empty — matches exactOptionalPropertyTypes' "strictly missing
    // OR string" contract on the RPC signature.
    const trimmedNotes = notes.trim();
    const args: Database['public']['Functions']['decide_exception']['Args'] = {
      p_order_id: row.order_id,
      p_exception_type: row.bucket,
      p_decision: decision,
      p_reason_code: reason,
      ...(trimmedNotes ? { p_notes: trimmedNotes } : {}),
    };
    const { error } = await client.rpc('decide_exception', args);
    setSubmitting(false);
    if (error) {
      captureSupabaseError(error, 'exceptions.decide.submit');
      setSubmitError('Could not record the decision. Please try again.');
      return;
    }
    onDecided();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={row ? `Decide — Order ${row.order_number}` : 'Decide'}
      titleId="decide-exception-title"
      footer={
        <>
          <Button variant="secondary" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="decide-exception-form"
            loading={submitting}
            disabled={!decision || !reason || submitting}
          >
            Record decision
          </Button>
        </>
      }
    >
      {row ? (
        <form
          id="decide-exception-form"
          onSubmit={handleSubmit}
          className="flex flex-col gap-4"
        >
          <div className="rounded-md bg-bg-soft p-3 text-xs text-ink-2">
            <div>
              <span className="text-ink-3">Patient:</span>{' '}
              <span className="text-ink">
                {row.patient_name ?? (
                  <span className="italic text-ink-3">No name on file</span>
                )}
              </span>
            </div>
            <div className="mt-0.5">
              <span className="text-ink-3">Bucket:</span>{' '}
              <span className="text-ink">{BUCKET_LABELS[row.bucket]}</span>{' '}
              <span className="text-ink-3">· raised {timeAgo(row.raised_at)}</span>
            </div>
          </div>

          {submitError ? <Alert tone="error">{submitError}</Alert> : null}

          <div className="flex flex-col gap-1">
            <label htmlFor="decide-decision" className="text-xs font-medium text-ink-2">
              Decision
            </label>
            <select
              id="decide-decision"
              value={decision}
              onChange={(e) => handleDecisionChange(e.target.value as Decision)}
              className="rounded-md border border-border bg-bg px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {availableDecisions.map((d) => (
                <option key={d} value={d}>
                  {DECISION_LABELS[d]}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label htmlFor="decide-reason" className="text-xs font-medium text-ink-2">
              Reason
            </label>
            <select
              id="decide-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value as ReasonCode)}
              className="rounded-md border border-border bg-bg px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {availableReasons.map((r) => (
                <option key={r} value={r}>
                  {REASON_LABELS[r]}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label htmlFor="decide-notes" className="text-xs font-medium text-ink-2">
              Notes <span className="text-ink-3">(optional)</span>
            </label>
            <textarea
              id="decide-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Anything worth capturing beyond the reason code?"
              className="resize-none rounded-md border border-border bg-bg px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        </form>
      ) : null}
    </Modal>
  );
}

// ----------------------------------------------------------------------------
// Bulk decision modal — same form shape as the single-decide, but
// applied to N rows via a sequential loop over decide_exception.
//
// v1 uses a client-side loop rather than a bulk RPC:
//   - decide_exception already has supersede semantics + type/decision
//     validation + audit-event write; wrapping it doesn't add
//     compliance value.
//   - Batches are expected to be small (a pharmacy triaging 5-20
//     exceptions), so N round trips is fine.
//   - Deferring the SQL bulk RPC keeps the prod DB touch out of this
//     PR (schema+RLS churn on prod is what we're avoiding after the
//     drift-repair from 2026-07-10). If batches grow, add a
//     bulk_decide_exception RPC in a follow-up migration.
//
// Failure handling: continue on error, summarize at the end. Partial
// success is fine because each decide_exception is atomic per order.

function BulkDecisionModal({
  open,
  rows,
  client,
  demoMode = false,
  onClose,
  onDecided,
}: {
  open: boolean;
  rows: ExceptionRow[];
  client: LynrxSupabaseClient;
  demoMode?: boolean;
  onClose: () => void;
  onDecided: () => void;
}): JSX.Element {
  // All rows share a bucket by the caller's contract (selection is only
  // enabled when a specific bucket filter is active). Default the form
  // to that bucket's first valid decision.
  const bucket = rows[0]?.bucket ?? 'failed_delivery';

  const [decision, setDecision] = useState<Decision | ''>('');
  const [reason, setReason] = useState<ReasonCode | ''>('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [summary, setSummary] = useState<{
    successes: number;
    failures: number;
  } | null>(null);

  useEffect(() => {
    if (!open) return;
    const first = DECISIONS_BY_BUCKET[bucket][0];
    setDecision(first ?? '');
    const reasons = first ? REASONS_BY_DECISION[first] : [];
    setReason(reasons[0] ?? '');
    setNotes('');
    setSummary(null);
  }, [open, bucket]);

  const availableDecisions = DECISIONS_BY_BUCKET[bucket];
  const availableReasons = decision ? REASONS_BY_DECISION[decision] : [];

  const handleDecisionChange = (next: Decision): void => {
    setDecision(next);
    const reasons = REASONS_BY_DECISION[next];
    setReason(reasons[0] ?? '');
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!decision || !reason || rows.length === 0) return;
    setSubmitting(true);
    setSummary(null);

    // Demo mode short-circuit — matches the single-decide modal.
    if (demoMode) {
      setSubmitting(false);
      onDecided();
      return;
    }

    const trimmedNotes = notes.trim();
    let successes = 0;
    let failures = 0;

    // Sequential — a pharmacy batch is small enough that serial keeps
    // things simple and preserves order in audit_events. If this ever
    // needs parallelism, move to Promise.allSettled with a concurrency
    // cap so we don't slam the RPC layer.
    for (const row of rows) {
      const args: Database['public']['Functions']['decide_exception']['Args'] = {
        p_order_id: row.order_id,
        p_exception_type: row.bucket,
        p_decision: decision,
        p_reason_code: reason,
        ...(trimmedNotes ? { p_notes: trimmedNotes } : {}),
      };
      const { error } = await client.rpc('decide_exception', args);
      if (error) {
        captureSupabaseError(error, 'exceptions.bulk.submit');
        failures += 1;
      } else {
        successes += 1;
      }
    }

    setSubmitting(false);
    if (failures === 0) {
      onDecided();
      return;
    }
    // Partial success — keep the modal open with a summary so the
    // pharmacist knows which decisions to retry. They can close;
    // the parent still refetches on close via the onClose path if
    // needed. For v1 we leave that manual — the summary is the signal.
    setSummary({ successes, failures });
    if (successes > 0) {
      // Successes did land — refresh the parent list so decisioned
      // items disappear, but keep this modal open with the summary.
      onDecided();
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Decide ${rows.length} orders`}
      titleId="bulk-decide-title"
      footer={
        <>
          <Button variant="secondary" type="button" onClick={onClose}>
            {summary ? 'Close' : 'Cancel'}
          </Button>
          <Button
            type="submit"
            form="bulk-decide-form"
            loading={submitting}
            disabled={!decision || !reason || submitting || summary !== null}
          >
            {summary ? 'Done' : `Record decision · ${rows.length}`}
          </Button>
        </>
      }
    >
      <form
        id="bulk-decide-form"
        onSubmit={handleSubmit}
        className="flex flex-col gap-4"
      >
        <div className="rounded-md bg-bg-soft p-3 text-xs text-ink-2">
          <div>
            <span className="text-ink-3">Bucket:</span>{' '}
            <span className="text-ink">{BUCKET_LABELS[bucket]}</span>{' '}
            <span className="text-ink-3">· {rows.length} orders</span>
          </div>
          <div className="mt-1 line-clamp-2 font-mono text-[10px] text-ink-3">
            {rows.map((r) => r.order_number).join(', ')}
          </div>
        </div>

        {summary ? (
          <Alert tone={summary.failures > 0 ? 'error' : 'success'}>
            {summary.successes} decisioned · {summary.failures} failed. Failed
            orders remain in the list — try again individually to see the
            per-order error.
          </Alert>
        ) : null}

        <div className="flex flex-col gap-1">
          <label htmlFor="bulk-decision" className="text-xs font-medium text-ink-2">
            Decision
          </label>
          <select
            id="bulk-decision"
            value={decision}
            onChange={(e) => handleDecisionChange(e.target.value as Decision)}
            disabled={submitting || summary !== null}
            className="rounded-md border border-border bg-bg px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-60"
          >
            {availableDecisions.map((d) => (
              <option key={d} value={d}>
                {DECISION_LABELS[d]}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="bulk-reason" className="text-xs font-medium text-ink-2">
            Reason
          </label>
          <select
            id="bulk-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value as ReasonCode)}
            disabled={submitting || summary !== null}
            className="rounded-md border border-border bg-bg px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-60"
          >
            {availableReasons.map((r) => (
              <option key={r} value={r}>
                {REASON_LABELS[r]}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="bulk-notes" className="text-xs font-medium text-ink-2">
            Notes <span className="text-ink-3">(optional — applied to every order)</span>
          </label>
          <textarea
            id="bulk-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            disabled={submitting || summary !== null}
            placeholder="Same note for all selected orders?"
            className="resize-none rounded-md border border-border bg-bg px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-60"
          />
        </div>
      </form>
    </Modal>
  );
}
